import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useRequiredCurrentUser } from 'FrontRoyalAngular';
import { getCameraSupported, cameraSources, CameraSource } from 'MobileFileUpload';
import { getAoiRecord } from 'Users';
import { ProgramType } from 'Program';
import { Avatar } from 'Avatar';
import { Button } from 'Button';
import { Text } from 'Text';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import { useAvatarUpload, AVATAR_MAX_SIZE_MB } from '../useAvatarUpload';

const ACCEPTED_FORMATS = 'image/jpeg,image/jpg,image/png,image/svg+xml,image/gif,image/webp';

const PROFILE_PHOTO_KEY = 'careers.edit_career_profile.profile_photo_form_section';

// React port of the Angular profile-photo-form-section + upload-avatar directive, scoped to the
// account page (LinkedIn sync is disabled there, so it's omitted). Web uses a file input; mobile
// (Cordova) uses the Capacitor camera.
export function AvatarUpload() {
    const { t } = useTranslation('back_royal');
    const currentUser = useRequiredCurrentUser();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { avatarUrl, loading, showAccessTip, oversized, uploadFile, getAndUploadPhoto } = useAvatarUpload();

    const isCareerNetworkOnly = getAoiRecord(currentUser)?.programType === ProgramType.career_network_only;
    const instructionsKey = isCareerNetworkOnly
        ? `${PROFILE_PHOTO_KEY}.upload_avatar_desc_alt`
        : `${PROFILE_PHOTO_KEY}.upload_avatar_desc_classmates_only`;
    const noteKey = isCareerNetworkOnly
        ? `${PROFILE_PHOTO_KEY}.upload_avatar_note_alt`
        : `${PROFILE_PHOTO_KEY}.upload_avatar_note_classmates_only`;

    const cameraSupported = getCameraSupported();

    const onFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        // Reset the input so selecting the same file again re-triggers change.
        event.target.value = '';
        if (file) uploadFile(file);
    };

    return (
        <div className="flex flex-col gap-2 py-6 sm:flex-row sm:gap-16">
            <div className="sm:w-1/3">
                <Text as="h2" color="black" weight="semibold" className="text-base">
                    {t(`${PROFILE_PHOTO_KEY}.profile_photo`)}
                </Text>
                <Text as="div" className="mt-1 text-sm">
                    {t(instructionsKey)}
                </Text>
            </div>
            <div className="flex items-start gap-6 sm:w-2/3">
                <Avatar src={avatarUrl ?? undefined} className="h-16 w-16 flex-shrink-0 sm:h-32 sm:w-32" />
                <div className="flex flex-col items-start gap-2">
                    {cameraSupported ? (
                        cameraSources
                            .filter(source => source !== CameraSource.Prompt)
                            .map(source => (
                                <Button
                                    key={source}
                                    color="primary"
                                    height={9}
                                    isDisabled={loading}
                                    onPress={() => getAndUploadPhoto(source)}
                                >
                                    {t(
                                        source === CameraSource.Camera
                                            ? 'front_royal_form.inputs.upload_avatar.camera_source_camera'
                                            : 'front_royal_form.inputs.upload_avatar.camera_source_photos',
                                    )}
                                </Button>
                            ))
                    ) : (
                        <>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept={ACCEPTED_FORMATS}
                                className="hidden"
                                onChange={onFileSelect}
                            />
                            <Button
                                color="primary"
                                height={9}
                                isDisabled={loading}
                                onPress={() => fileInputRef.current?.click()}
                            >
                                {t('front_royal_form.inputs.upload_avatar.upload')}
                            </Button>
                        </>
                    )}
                    {showAccessTip ? (
                        <Text as="div" variant="small" className="text-red-500">
                            {t('front_royal_form.inputs.upload_avatar.picture_access_tip')}
                        </Text>
                    ) : null}
                    <Text as="div" variant="small">
                        {t(noteKey)}
                    </Text>
                    <Text as="div" variant="small" className={`italic ${oversized ? 'text-red-500' : ''}`}>
                        {t('settings.edit_account.avatar_max_size', { size: `${AVATAR_MAX_SIZE_MB}MB` })}
                    </Text>
                    {loading ? <FrontRoyalSpinner className="no-delay no-top-margin inline" /> : null}
                </div>
            </div>
        </div>
    );
}
